import React, { useState, useEffect, useCallback, useRef } from 'react';
import DataEditor from './DataEditor';
import Button from './Button';
import { apiGet, apiPut, apiPost } from '../api';
import { useAccessTokens } from '../Authenticate';
import {
    Grid,
    Typography,
    Dialog,
    DialogActions,
    Snackbar,
    SnackbarContent,
    Card,
    CardContent,
    Tooltip,
    IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import MaterialTable from 'material-table';

const useStyles = makeStyles((theme) => ({
    card: {
        width: '55vw',
    },
    headerButton: {
        width: '32px',
        height: '32px',
        margin: theme.spacing(1),
    },
    headerButtons: {
        margin: theme.spacing(2),
    },
    configureBody: {
        margin: theme.spacing(2),
    },
    dialog: {
        margin: theme.spacing(3),
    },
    headerTitle: {
        margin: theme.spacing(3),
    },
}));

export default function EntityTab({ entityType }) {
    const classes = useStyles();
    const { getAccessToken, authorizationScopes } = useAccessTokens();

    const collectionName = getCollectionName(entityType);
    const apiName = getApiName(entityType);

    const [entityList, setEntityList] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [snackbarInfo, setSnackbarInfo] = useState({ message: 'message', open: false, color: '#000000' });

    const currentEntityRef = useRef({});

    // turns 'LicenseTypes' to 'License Types'
    function getCollectionName(name) {
        let uppercaseIndexes = name.match(/[A-Z]/g).map((capLetter) => {
            return name.indexOf(capLetter);
        });
        return uppercaseIndexes.length === 1
            ? name
            : name.slice(0, uppercaseIndexes[1]) + ' ' + name.slice(uppercaseIndexes[1], name.length - 1);
    }

    // turns 'LicenseTypes' to 'licenseTypes'
    function getApiName(name) {
        let firstLetter = name.charAt(0);
        return firstLetter !== -1 ? firstLetter.toLowerCase() + name.slice(1) : '';
    }

    // callback to API to overwrite local changes or just fetch the data initially
    const fetchData = useCallback(
        async (callbackFunction) => {
            try {
                const token = await getAccessToken(authorizationScopes[apiName]);
                const result = await apiGet(apiName, token);
                setEntityList(result.data);
                callbackFunction && callbackFunction();
            } catch (err) {
                setSnackbarInfo({ message: 'Error loading data', open: true, color: '#f44336' });
            }
        },
        [authorizationScopes, getAccessToken, apiName],
    );

    async function updateData(data) {
        try {
            const token = await getAccessToken(authorizationScopes[apiName]);
            data.id ? await apiPut(`${apiName}/${data.id}`, data, token) : await apiPost(apiName, data, token);
            await fetchData(() =>
                setSnackbarInfo({ message: `${collectionName} Saved`, open: true, color: '#66bb6a' }),
            );
            setDialogOpen(false);
        } catch (err) {
            setSnackbarInfo({ message: 'Error saving data', open: true, color: '#f44336' });
        }
    }

    useEffect(() => {
        async function runFetchData() {
            await fetchData();
        }
        runFetchData();
    }, [fetchData]);

    // Function to determine the selected data in the material table
    function selectData(item) {
        const { tableData, ...rest } = item;
        setDialogTitle(`Edit ${collectionName}`);

        currentEntityRef.current = rest;

        setDialogOpen(true); // Open JSON Editor Dialog
    }

    const createNewEntity = () => {
        setDialogTitle(`New ${collectionName}`);

        currentEntityRef.current = {};

        setDialogOpen(true);
    };

    // Function when refresh button is clicked
    async function refreshData() {
        await fetchData(() =>
            setSnackbarInfo({
                message: 'Data Refreshed',
                open: true,
                color: '#000000',
            }),
        );
    }

    const snackBar = (
        <Snackbar
            open={snackbarInfo.open}
            autoHideDuration={1500}
            onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
        >
            <SnackbarContent
                style={{
                    backgroundColor: snackbarInfo.color,
                }}
                message={snackbarInfo.message}
            />
        </Snackbar>
    );

    const dataTable = (
        <MaterialTable
            options={{
                toolbar: false,
                paging: false,
                draggable: false,
                sorting: true,
            }}
            style={{ boxShadow: 'none', paddingLeft: 24, paddingRight: 24 }}
            localization={{
                body: {
                    emptyDataSourceMessage: 'No Data to Display',
                },
            }}
            columns={[
                { title: 'ID', field: 'id' },
                { title: 'Name', field: 'name' },
            ]}
            data={entityList}
            onRowClick={(_, item) => selectData(item)}
        />
    );

    const openEditor = (
        <Dialog open={dialogOpen} fullWidth={true} maxWidth="md" className={classes.dialog}>
            <Grid container>
                <Typography variant="h5" className={classes.dialog}>
                    {dialogTitle}
                </Typography>
            </Grid>

            <DataEditor data={currentEntityRef.current} handleUpdate={(data) => (currentEntityRef.current = data)} />

            <DialogActions className={classes.dialog}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setDialogOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="containedPrimary" // This is where error comes from.
                    onClick={async () => {
                        await updateData(currentEntityRef.current);
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Grid container justifyContent="center" alignItems="center">
            {snackBar}
            {openEditor}

            <div className={classes.configureBody}>
                <Card className={classes.card}>
                    <CardContent>
                        <Grid container justifyContent="space-between">
                            <Typography variant="h5" className={classes.headerTitle}>
                                {collectionName}
                            </Typography>
                            <div className={classes.headerButtons}>
                                <Tooltip title="Refresh Data">
                                    <IconButton
                                        onClick={refreshData}
                                        aria-label="Refresh"
                                        className={classes.headerButton}
                                        style={{ backgroundColor: '#E0E0E0' }}
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={`Create New ${collectionName}`}>
                                    <IconButton
                                        onClick={createNewEntity}
                                        aria-label="Add"
                                        className={classes.headerButton}
                                        style={{ backgroundColor: '#0277BD', color: 'white' }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </Grid>
                        <div className={classes.dataTable}>{dataTable}</div>
                    </CardContent>
                </Card>
            </div>
        </Grid>
    );
}
